import type { SupportPartCommand } from '@/pages/[org_id]/support/use-support-chat'

/**
 * Put a part on screen from the console, in development only.
 *
 * WHY THIS EXISTS. Whether a part appears is the agent's decision, so
 * looking at one meant getting a model to choose a tool, which meant
 * finding an organisation with two similarly named venues and phrasing a
 * question that came out ambiguous. That is a terrible loop for judging
 * whether a sheet clears the home indicator on a phone.
 *
 * The geometry is the risk on this surface, not the plumbing. This makes
 * the geometry inspectable in a second:
 *
 *   __agentPart()                       a choice of two, as a sheet
 *   __agentPart({ mode: 'stage' })      the same, taking the screen
 *   __agentPart({ options: 5 })         five, which asks for more height
 *
 * STRIPPED IN PRODUCTION. Gated on `import.meta.env.DEV`, so the bundler
 * drops the whole module rather than shipping a way to fake agent
 * instructions into somebody's browser.
 */

const VENUES = [
  { label: 'The Bothy Perth', detail: 'Perth · 48 covers' },
  { label: 'The Bothy Glasgow', detail: 'Glasgow · 72 covers' },
  { label: 'Tigerlily', detail: 'Edinburgh · 120 covers' },
  { label: "Benassi's", detail: 'Edinburgh · 60 covers' },
  { label: 'The Huxley', detail: 'Edinburgh · 90 covers' },
]

/**
 * A choice ABOUT A PLACE, which is the one that moves optimistically.
 *
 * `__agentPart({ pages: true })`. Routes are relative so they work in
 * whatever organisation the dock is open in, which is the only reason a
 * fake is allowed to invent one.
 */
const PAGES = [
  { label: 'Email Templates', detail: 'Bookings › Email Templates' },
  { label: 'Email Campaigns', detail: 'Marketing › Email Campaigns' },
  { label: 'Email Audit', detail: 'Email Audit' },
]

export function installDevParts(
  onPart: (command: SupportPartCommand) => void,
): () => void {
  if (!import.meta.env.DEV) return () => undefined

  const fake = (
    opts: { options?: number; mode?: 'panel' | 'stage'; pages?: boolean } = {},
  ) => {
    const source = opts.pages ? PAGES : VENUES
    const count = Math.min(Math.max(opts.options ?? 2, 2), source.length)
    const base = window.location.pathname.split('/').slice(0, 2).join('/')
    onPart({
      part: {
        kind: 'choose',
        prompt: opts.pages
          ? 'Which of these did you mean?'
          : 'Which Bothy did you mean?',
        options: source.slice(0, count).map((option, i) =>
          opts.pages
            ? {
                ...option,
                goTo: {
                  path: `${base}/${['bookings', 'marketing', 'reports'][i] ?? 'bookings'}`,
                  label: option.detail ?? option.label,
                  pageId: `dev-${String(i)}`,
                },
              }
            : option,
        ),
      },
      mode: opts.mode ?? 'panel',
      height: count > 3 ? 'half' : 'peek',
    })
    return `showing ${String(count)} options as ${opts.mode ?? 'panel'}`
  }

  Object.defineProperty(window, '__agentPart', {
    value: fake,
    configurable: true,
    writable: true,
  })
  return () => {
    delete (window as unknown as Record<string, unknown>).__agentPart
  }
}
